/**
 * SeriesOccurrenceRow — one date in the series list: its number, its date and
 * start time (both editable), and an Undo once it has been changed by hand.
 * The end time is not edited here; every workshop keeps the series' length.
 */
import { DateField } from '../../primitives/DateField';
import { TimeField } from '../../primitives/TimeField';

import type { SeriesEdit, SeriesOccurrence } from './workshopCreateSeries';

const UNDO: React.CSSProperties = {
  height: 32,
  padding: '0 8px',
  border: 'none',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--nb-primary)',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};

export function SeriesOccurrenceRow({
  occurrence,
  minDate,
  onEdit,
  onReset,
}: {
  occurrence: SeriesOccurrence;
  minDate: string;
  onEdit: (index: number, field: keyof SeriesEdit, value: string) => void;
  onReset: (index: number) => void;
}) {
  const { index, date, start, edited, problem } = occurrence;
  const n = index + 1;
  return (
    <li style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '22px minmax(0, 1fr) 112px 52px',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ fontSize: 11, color: 'var(--nb-subtle)', fontWeight: 700 }}>{n}.</span>
        <DateField
          ariaLabel={`Workshop ${n} date`}
          value={date}
          min={minDate}
          error={!!problem}
          onChange={(v) => onEdit(index, 'date', v)}
        />
        <TimeField
          ariaLabel={`Workshop ${n} start`}
          value={start}
          error={!!problem}
          onChange={(v) => onEdit(index, 'start', v)}
        />
        {edited ? (
          <button
            type="button"
            aria-label={`Undo changes to workshop ${n}`}
            onClick={() => onReset(index)}
            style={UNDO}
          >
            Undo
          </button>
        ) : (
          <span />
        )}
      </div>
      {problem && (
        <span role="alert" style={{ fontSize: 10.5, color: 'var(--nb-risk)', paddingLeft: 28 }}>
          {problem}
        </span>
      )}
    </li>
  );
}
